import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';

import { requestNotificationPermission } from '../shared/services/onesignalService';

/**
 * Ask for push permission at a moment the player has just had a good experience —
 * right after finishing a quiz — instead of on first launch.
 *
 * iOS shows its system permission dialog exactly once per install: a "no" is permanent
 * until the player digs through Settings. Asking before the player has seen what the app
 * does spends that single chance when they are least likely to say yes. Daily-reminder
 * pushes are the app's main re-engagement channel, so the opt-in rate matters.
 *
 * Rules:
 *  - at most once per install (persisted), and never in development;
 *  - never on web;
 *  - the flag is written BEFORE the request, so a crash mid-dialog cannot re-ask forever.
 *
 * Every failure path is swallowed: a permission nicety must never break the results screen.
 */
const ASKED_KEY = 'push_prompt_asked_v1';

let askedThisSession = false;

/** True once this app session has already shown (or tried to show) the push dialog. */
export function askedForPushThisSession(): boolean {
  return askedThisSession;
}

/**
 * Request push permission if this install has not been asked yet.
 * @returns true when permission is granted (or already was).
 */
export async function maybeRequestPushAfterQuiz(): Promise<boolean> {
  try {
    if (Platform.OS !== 'ios' && Platform.OS !== 'android') return false;
    if (__DEV__) return false;
    if (askedThisSession) return false;
    if (await AsyncStorage.getItem(ASKED_KEY)) return false;

    askedThisSession = true;
    await AsyncStorage.setItem(ASKED_KEY, new Date().toISOString());
    return await requestNotificationPermission();
  } catch {
    return false;
  }
}

/** Test seam: forget the in-memory session flag. */
export function __resetPushPromptSession(): void {
  askedThisSession = false;
}
